import { TPosition } from '@components/typescript';
import theme from '@styles/theme';

const normalizeButtonHorizontal = (position: string) => {
  if (position === 'start' || position === 'left') return 'left';
  if (position === 'end' || position === 'right') return 'right';
  return 'center';
};

const sliderButtonJustify = (position: string) => {
  const horizontal = normalizeButtonHorizontal(position);
  return horizontal === 'left' ? 'flex-start' : horizontal === 'right' ? 'flex-end' : 'center';
};

export default {
  sliderContainer: (isDividedOnMobile: boolean) => {
    const mobileHeight = isDividedOnMobile ? '100%' : '716px';
    return {
      position: 'relative',
      height: ['100%', '100%', '1024px', '1024px', '46.875vw'],
      maxHeight: [mobileHeight, mobileHeight, '1024px', '1024px', '46.875vw'],
      background: theme.background.section,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'space-between',
    };
  },
  sliderImageContainer: (fullHeight: boolean) => ({
    width: '100vw',
    height: fullHeight ? '100%' : ['520px', '520px', '1024px', '1024px', '46.875vw'],
    maxHeight: fullHeight ? '100%' : ['520px', '520px', '1024px', '1024px', '46.875vw'],
  }),
  sliderImage: {
    height: '100%',
    width: '100%',
    position: 'relative',

    img: {
      transition: 'opacity .15s',
      objectPosition: 'center',
      height: '100%',
    },
  },
  canvas: {
    height: ['514px', '514px', '1024px', '1024px', '46.875vw'],
    width: '100%',
  },
  sliderContentContainer: (position: TPosition) => {
    const getAlignment = (alignment) => (alignment !== 'center' ? `flex-${alignment}` : alignment);
    return {
      alignItems: getAlignment(position.horizontal),
      justifyContent: position.vertical ? ['center', 'center', getAlignment(position.vertical)] : 'center',
      padding: ['40px', '40px', '156px 40px 40px', '156px 40px 40px', '80px'],
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: '0',
      left: '0',
    };
  },
  textImgContainer: {
    position: 'relative',
    width: ['244px', '244px', '390px'],
    margin: 'auto',
    img: {
      width: ['244px', '244px', '390px'],
      height: 'auto',
    },
  },
  sliderTitleContainer: (position: TPosition) => {
    const getAlignment = (alignment) => (alignment !== 'center' ? `flex-${alignment}` : alignment);
    return {
      padding: '40px 20px',
      '&.display__three': {
        fontSize: [theme.fontSizes.h1, '72px'],
        alignItems: ['center', getAlignment(position.vertical)],
        justifyContent: ['center', getAlignment(position.vertical)],
        padding: ['0', '80px'],
        mt: ['0', 0],
        display: 'flex',
        flexDirection: 'column',
        position: ['static', 'absolute'],
        width: '100%',
        height: '100%',
      },
    };
  },
  linkContainer: {
    width: '100%',
  },
  sliderTopText: (position: TPosition, color?: string) => ({
    fontSize: theme.fontSizes.bigText,
    fontWeight: theme.fontWeights.normal,
    color: color || theme.rawColors.neutralWhite,
    display: 'flex',
    textAlign: ['center', `${position.horizontal}`],
    justifyContent: ['center', `flex-${position.horizontal}`],
  }),
  sliderDescription: (position: TPosition, color?: string) => ({
    fontSize: [theme.fontSizes.h4, theme.fontSizes.h4, theme.fontSizes.h4, theme.fontSizes.h4, theme.fontSizes.h3],
    lineHeight: '140%',
    mt: '16px',
    width: '100%',
    color: color || theme.rawColors.neutralWhite,
    textAlign: ['center', `${position.horizontal}`],
    letterSpacing: theme.letterSpacings.caps,
    padding: ['0 16px', '0 16px', '0 100px', '0 100px', '0'],
  }),
  sliderArrowControl: (dir: 'left' | 'right', hidden = false) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: hidden ? 0 : theme.zIndex.documentUp,
    opacity: hidden ? 0 : 1,
    [dir]: [0, 0, 0, '12px'],
  }),
  buttonLinkPosition: (buttonPosition = 'center', buttonVerticalPosition = 'center', isVoxboxLink?: boolean) => {
    const horizontal = normalizeButtonHorizontal(buttonPosition);
    return {
      position: ['absolute', 'absolute', 'relative'],
      bottom: '0',
      height: ['106px', '106px', '122px', '70px'],
      width: '100%',
      left: horizontal === 'center' ? ['50%', '50%', 'auto'] : 'auto',
      transform: horizontal === 'center' ? ['translateX(-50%)', 'translateX(-50%)', 'none'] : 'none',
      mt: buttonVerticalPosition !== 'start' ? '40px' : 'unset',
      mb: buttonVerticalPosition === 'start' ? '40px' : 'unset',
      display: [isVoxboxLink ? 'none' : 'flex', isVoxboxLink ? 'none' : 'flex', isVoxboxLink ? 'none' : 'flex', 'flex'],
      justifyContent: buttonPosition === 'top' ? 'center' : sliderButtonJustify(buttonPosition),
      zIndex: theme.zIndex.documentUp,
      alignSelf:
        horizontal === 'left'
          ? ['auto', 'auto', 'flex-start']
          : horizontal === 'right'
            ? ['auto', 'auto', 'flex-end']
            : ['auto', 'auto', 'center'],
    };
  },
  buttonAdditionalLinkPosition: (buttonPosition: string, buttonVerticalPosition: string) => ({
    position: ['absolute', 'absolute', 'relative'],
    bottom: '0',
    width: '100%',
    left: buttonPosition === 'center' ? ['50%', '50%', 'auto'] : 'auto',
    transform: buttonPosition === 'center' ? ['translateX(-50%)', 'translateX(-50%)', 'none'] : 'none',
    mt: buttonVerticalPosition !== 'start' ? '40px' : 'unset',
    mb: buttonVerticalPosition === 'start' ? '40px' : ['48px', '48px', '0'],
    display: 'flex',
    flexFlow: ['column', 'column', 'row'],
    justifyContent: buttonPosition === 'top' ? 'center' : buttonPosition,
    zIndex: theme.zIndex.documentUp,
  }),
  additionalText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    m: ['16px 0', '16px 0', '0 16px', '0 16px', '0 24px'],
    span: {
      color: theme.rawColors.neutralWhite,
      fontSize: theme.fontSizes.h4,
    },
  },
  additionalButtonConatiner: (isVoxboxLink?: boolean) => ({
    display: [isVoxboxLink ? 'none' : 'flex', isVoxboxLink ? 'none' : 'flex', isVoxboxLink ? 'none' : 'flex', 'flex'],
    justifyContent: 'center',
    alignItems: 'center',
  }),
  mobileSliderContent: {
    textAlign: 'center',
    background: theme.rawColors.lightGrey300,
    padding: '40px 16px 16px',
    width: '100%',
    h1: {
      fontSize: theme.fontSizes.h1,
      letterSpacing: theme.letterSpacings.caps,
      textTransform: 'uppercase',
    },
    p: {
      fontSize: theme.fontSizes.h4,
      p: '16px 0 0',
    },
  },
  sliderIndicatorsWrapper: {
    display: 'inline-flex',
    position: 'absolute',
    zIndex: theme.zIndex.documentHigh,
    bottom: '24px',
    right: ['50%', '50%', '50%', '50%', '24px'],
    transform: ['translateX(50%)', 'translateX(50%)', 'translateX(50%)', 'translateX(50%)', 'translateX(0%)'],
  },
  sliderIndicator: (isActive: boolean) => ({
    display: 'inline-flex',
    width: '9px',
    height: '6px',
    zIndex: theme.zIndex.documentHigh + 1,
    border: 'none',
    cursor: 'pointer',
    m: '4px',
    background: isActive ? theme.rawColors.darkGrey700 : theme.rawColors.lightGrey500,
  }),
};
